import React, { useState, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiErrorMessage, isApiSuccess } from '../../utils/apiResponse';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { 
  X, 
  Send, 
  Camera, 
  BugIcon, 
  Lightbulb, 
  HelpCircle, 
  ThumbsUp, 
  AlertTriangle,
  CheckCircle
} from 'lucide-react';

const FeedbackForm = ({ isOpen, onClose }) => {
  const { currentUser, getAuthenticatedApi } = useAuth();
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  
  const [formState, setFormState] = useState({
    type: 'bug', // bug, suggestion, question, appreciation
    title: '',
    description: '',
    email: currentUser?.email || '',
    severity: 'medium', // low, medium, high, critical
    attachments: [],
    deviceInfo: {
      browser: navigator.userAgent,
      screenSize: `${window.innerWidth}x${window.innerHeight}`,
      platform: navigator.platform
    }
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  if (!isOpen) return null;
  
  // Form validation
  const validateForm = () => {
    const newErrors = {};
    
    if (!formState.title.trim()) {
      newErrors.title = t('feedback.errors.titleRequired');
    }
    
    if (!formState.description.trim()) {
      newErrors.description = t('feedback.errors.descriptionRequired');
    } else if (formState.description.trim().length < 10) {
      newErrors.description = t('feedback.errors.descriptionTooShort');
    }
    
    if (!formState.email.trim()) {
      newErrors.email = t('feedback.errors.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(formState.email)) {
      newErrors.email = t('feedback.errors.emailInvalid');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState({
      ...formState,
      [name]: value
    });
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: undefined
      });
    }
  };
  
  // Handle file uploads
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Validate files (max 3 files, each max 5MB)
    if (formState.attachments.length + files.length > 3) {
      setErrors({
        ...errors,
        attachments: t('feedback.errors.tooManyFiles')
      });
      return;
    }
    
    const invalidFiles = files.filter(file => file.size > 5 * 1024 * 1024);
    if (invalidFiles.length > 0) {
      setErrors({
        ...errors,
        attachments: t('feedback.errors.fileTooLarge')
      });
      return;
    }
    
    // Add files to state
    setFormState({
      ...formState,
      attachments: [...formState.attachments, ...files]
    });
    
    // Clear error
    if (errors.attachments) {
      setErrors({
        ...errors,
        attachments: undefined
      });
    }
    
    // Log per debug
    console.log('Files caricati:', files.map(f => ({ name: f.name, size: f.size, type: f.type })));
  };
  
  // Remove an attachment
  const removeAttachment = (index) => {
    const newAttachments = [...formState.attachments];
    newAttachments.splice(index, 1);
    setFormState({
      ...formState,
      attachments: newAttachments
    });
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setUploadProgress(0);
    
    try {
      // Usa l'API per inviare il feedback
      const api = getAuthenticatedApi();
      
      // Inviamo con una di queste due metodologie, a seconda se ci sono allegati
      if (formState.attachments.length > 0) {
        // Utilizziamo FormData per l'invio con gli allegati
        const formData = new FormData();
        
        // Aggiungiamo i campi del form
        formData.append('type', formState.type);
        formData.append('title', formState.title);
        formData.append('description', formState.description);
        formData.append('email', formState.email);
        formData.append('severity', formState.severity);
        formData.append('device_info', JSON.stringify(formState.deviceInfo));
        
        // Aggiungiamo gli allegati - modificato per essere più esplicito con i nomi
        formState.attachments.forEach((file) => {
          formData.append('attachments[]', file);
        });
        
        // Log per debug
        console.log('FormData creato con campi:', 
          Array.from(formData.entries()).map(([key]) => key));
        
        // Inviamo i dati con monitoraggio dell'upload
        const response = await api.post('/feedback_api.php', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            console.log(`Upload progress: ${percentCompleted}%`);
            setUploadProgress(percentCompleted);
          }
        });
        
        console.log('Risposta ricevuta:', response.data);
        
        // Gestiamo la risposta
        if (isApiSuccess(response.data)) {
          setIsSuccess(true);
          
          // Reset form dopo il successo
          setTimeout(() => {
            setIsSuccess(false);
            onClose();
            
            // Reset form state per la prossima volta
            setFormState({
              ...formState,
              type: 'bug',
              title: '',
              description: '',
              severity: 'medium',
              attachments: []
            });
          }, 2000);
        } else {
          throw new Error(apiErrorMessage(response.data, 'Errore sconosciuto'));
        }
      } else {
        // Senza allegati, usiamo JSON standard
        const response = await api.post('/feedback_api.php', {
          type: formState.type,
          title: formState.title,
          description: formState.description,
          email: formState.email,
          severity: formState.severity,
          deviceInfo: formState.deviceInfo
        });
        
        // Gestiamo la risposta
        if (isApiSuccess(response.data)) {
          setIsSuccess(true);
          
          // Reset form dopo il successo
          setTimeout(() => {
            setIsSuccess(false);
            onClose();
            
            // Reset form state per la prossima volta
            setFormState({
              ...formState,
              type: 'bug',
              title: '',
              description: '',
              severity: 'medium',
              attachments: []
            });
          }, 2000);
        } else {
          throw new Error(apiErrorMessage(response.data, 'Errore sconosciuto'));
        }
      }
    } catch (error) {
      console.error('Errore nell\'invio del feedback:', error);
      setErrors({
        ...errors,
        submit: error.message || t('common.connectionError')
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Get icon based on feedback type
  const getFeedbackTypeIcon = (type) => {
    switch (type) {
      case 'bug':
        return <BugIcon className="h-5 w-5 text-red-500 dark:text-red-400" />;
      case 'suggestion':
        return <Lightbulb className="h-5 w-5 text-yellow-500 dark:text-yellow-400" />;
      case 'question':
        return <HelpCircle className="h-5 w-5 text-blue-500 dark:text-blue-400" />;
      case 'appreciation':
        return <ThumbsUp className="h-5 w-5 text-green-500 dark:text-green-400" />;
      default:
        return <BugIcon className="h-5 w-5 text-red-500 dark:text-red-400" />;
    }
  };
  
  // Get severity badge style
  const getSeverityStyle = (severity) => {
    switch (severity) {
      case 'low':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'high':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
      case 'critical':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div 
        className={`relative max-w-2xl w-full rounded-xl shadow-lg ${
          isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
        } max-h-[90vh] overflow-hidden`}
      >
        {/* Success message overlay */}
        {isSuccess && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white dark:bg-gray-800 bg-opacity-95 dark:bg-opacity-95">
            <div className="w-16 h-16 mb-4 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              {t('feedback.successTitle')}
            </h3>
            <p className="text-gray-600 dark:text-gray-300">
              {t('feedback.successMessage')}
            </p>
          </div>
        )}
        
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-xl font-bold">{t('feedback.title')}</h2>
          <button 
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            disabled={isSubmitting}
          >
            <X className="h-6 w-6 text-gray-500 dark:text-gray-400" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 80px)' }}>
          {/* Errore generale */}
          {errors.submit && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
              {errors.submit}
            </div>
          )}
          
          {/* Feedback Types */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">
              {t('feedback.typeLabel')}
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {['bug', 'suggestion', 'question', 'appreciation'].map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setFormState({ ...formState, type })}
                  className={`flex flex-col items-center justify-center p-3 rounded-lg border ${
                    formState.type === type
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30 dark:border-indigo-400'
                      : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {getFeedbackTypeIcon(type)}
                  <span className="mt-2 text-sm">{t(`feedback.types.${type}`)}</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Title */}
          <div className="mb-4">
            <label htmlFor="title" className="block text-sm font-medium mb-1">
              {t('feedback.titleLabel')} *
            </label>
            <input
              id="title"
              name="title"
              type="text"
              value={formState.title}
              onChange={handleChange}
              placeholder={t('feedback.titlePlaceholder')}
              className={`w-full p-3 border rounded-lg ${
                isDark 
                  ? 'bg-gray-700 border-gray-600' 
                  : 'bg-white border-gray-300'
              } ${errors.title ? 'border-red-500 dark:border-red-400' : ''}`}
              disabled={isSubmitting}
            />
            {errors.title && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.title}</p>
            )}
          </div>
          
          {/* Description */}
          <div className="mb-4">
            <label htmlFor="description" className="block text-sm font-medium mb-1">
              {t('feedback.descriptionLabel')} *
            </label>
            <textarea
              id="description"
              name="description"
              value={formState.description}
              onChange={handleChange}
              placeholder={t('feedback.descriptionPlaceholder')}
              rows={5}
              className={`w-full p-3 border rounded-lg ${
                isDark 
                  ? 'bg-gray-700 border-gray-600' 
                  : 'bg-white border-gray-300'
              } ${errors.description ? 'border-red-500 dark:border-red-400' : ''}`}
              disabled={isSubmitting}
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.description}</p>
            )}
          </div>
          
          {/* Email */}
          <div className="mb-4">
            <label htmlFor="email" className="block text-sm font-medium mb-1">
              {t('feedback.emailLabel')} *
            </label>
            <input
              id="email"
              name="email"
              type="email"
              value={formState.email}
              onChange={handleChange}
              placeholder={t('feedback.emailPlaceholder')}
              className={`w-full p-3 border rounded-lg ${
                isDark 
                  ? 'bg-gray-700 border-gray-600' 
                  : 'bg-white border-gray-300'
              } ${errors.email ? 'border-red-500 dark:border-red-400' : ''}`}
              disabled={isSubmitting}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.email}</p>
            )}
          </div>
          
          {/* Severity (only for bugs) */}
          {formState.type === 'bug' && (
            <div className="mb-4">
              <label htmlFor="severity" className="block text-sm font-medium mb-1">
                {t('feedback.severityLabel')}
              </label>
              <div className="flex flex-wrap gap-2">
                {['low', 'medium', 'high', 'critical'].map((severity) => (
                  <button
                    key={severity}
                    type="button"
                    onClick={() => setFormState({ ...formState, severity })}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                      formState.severity === severity
                        ? getSeverityStyle(severity) + ' ring-2 ring-offset-2 ring-offset-white dark:ring-offset-gray-800 ring-current'
                        : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                    }`}
                    disabled={isSubmitting}
                  >
                    {severity === 'critical' && <AlertTriangle className="h-3.5 w-3.5 inline mr-1" />}
                    {t(`feedback.severityLevels.${severity}`)}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Attachments */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-1">
              {t('feedback.attachmentsLabel')}
            </label>
            
            <div className="flex items-center gap-3 mb-2">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className={`px-4 py-2 flex items-center justify-center rounded-lg border ${
                  isDark 
                    ? 'border-gray-600 hover:bg-gray-700 text-gray-300' 
                    : 'border-gray-300 hover:bg-gray-100 text-gray-700'
                }`}
                disabled={isSubmitting || formState.attachments.length >= 3}
              >
                <Camera className="h-4 w-4 mr-2" />
                {t('feedback.addAttachment')}
              </button>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('feedback.attachmentLimit', { current: formState.attachments.length, max: 3 })}
              </p>
            </div>
            
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept="image/*,.pdf,.doc,.docx"
              multiple
              className="hidden"
              disabled={isSubmitting}
            />
            
            {formState.attachments.length > 0 && (
              <div className="space-y-2 mt-2">
                {formState.attachments.map((file, index) => (
                  <div 
                    key={index}
                    className={`flex items-center justify-between p-2 rounded ${
                      isDark ? 'bg-gray-700' : 'bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center overflow-hidden">
                      <span className="text-sm truncate">{file.name}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                        {(file.size / 1024).toFixed(1)} KB
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                      disabled={isSubmitting}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {errors.attachments && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.attachments}</p>
            )}
          </div>
          
          {/* Upload Progress */}
          {isSubmitting && uploadProgress > 0 && (
            <div className="mb-4">
              <div className="flex justify-between mb-1">
                <span className="text-sm font-medium">{t('feedback.uploading')}...</span>
                <span className="text-sm font-medium">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div 
                  className="bg-indigo-600 h-2 rounded-full" 
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          )}
          
          {/* Automatic system information */}
          <div className="mb-6">
            <div className="flex items-center mb-1">
              <h3 className="text-sm font-medium">
                {t('feedback.systemInfoLabel')}
              </h3>
              <span className="text-xs bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-2 py-0.5 rounded ml-2">
                {t('feedback.autoCollected')}
              </span>
            </div>
            <div className={`text-xs p-3 rounded-lg ${
              isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
            }`}>
              <p><strong>{t('feedback.systemInfo.browser')}:</strong> {formState.deviceInfo.browser}</p>
              <p><strong>{t('feedback.systemInfo.resolution')}:</strong> {formState.deviceInfo.screenSize}</p>
              <p><strong>{t('feedback.systemInfo.platform')}:</strong> {formState.deviceInfo.platform}</p>
            </div>
          </div>
          
          {/* Submit button */}
          <div className="flex justify-end">
            <button
              type="submit"
              className={`py-2 px-6 rounded-lg flex items-center font-medium ${
                isSubmitting
                  ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed'
                  : 'bg-indigo-600 hover:bg-indigo-700 text-white'
              }`}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t('feedback.sending')}
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  {t('feedback.submit')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeedbackForm;